import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';

import type {
  ClaimFreeTicketMutation,
  ClaimFreeTicketMutationVariables,
  FreeTicket_TaskInterface,
} from './__generated__/index.graphql';

const CLAIM_FREE_TICKET_MUTATION = gql`
  mutation ClaimFreeTicketMutation($taskId: ID!, $sport: Sport!) {
    claimTask(input: { taskId: $taskId }) {
      task {
        id
        aasmState
      }
      currentUser {
        slug
        wheelTickets: inGameCurrencyBalances(
          sport: $sport
          inGameCurrencies: [WHEEL_TICKET]
        ) {
          amount
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  ClaimFreeTicketMutation,
  ClaimFreeTicketMutationVariables
>;

type Props = {
  task?: FreeTicket_TaskInterface;
  nextFreeTicketIn?: Date;
};

export const FreeTicket = ({ task, nextFreeTicketIn }: Props) => {
  const ticketAvailable =
    task?.aasmState === ManagerProgressionTaskState.COMPLETED;
  const { formatMessage } = useIntl();
  const { sport } = useSportContext();

  const [claimTask, { loading }] = useMutation(CLAIM_FREE_TICKET_MUTATION, {
    showErrorsWithSnackNotification: true,
  });

  const canBeDisplayed = nextFreeTicketIn && task?.id;

  return (
    <Vertical center>
      <LabelM color="var(--c-nd-600)">
        <FormattedMessage
          id="marketplace.wheel.freeTicket.title"
          defaultMessage="Free ticket"
        />
      </LabelM>

      <Button
        color="primary"
        size="small"
        onClick={
          canBeDisplayed
            ? async () => {
                if (task) {
                  await claimTask({
                    variables: { taskId: task.id, sport },
                  });
                }
              }
            : undefined
        }
        disabled={loading || !ticketAvailable}
      >
        <FormattedMessage
          id="marketplace.wheel.freeTicket.button"
          defaultMessage="Claim"
        />
      </Button>

      {!!canBeDisplayed && !!ticketAvailable && (
        <LabelS color="var(--c-nd-600)">
          <FormattedMessage
            id="marketplace.wheel.freeTicket.ready"
            defaultMessage="Ready"
          />
        </LabelS>
      )}

      {!!canBeDisplayed && !ticketAvailable && (
        <LabelS color="var(--c-nd-600)">
          <TimeLeft
            time={nextFreeTicketIn}
            endLabel={formatMessage({
              id: 'marketplace.wheel.freeTicket.ready',
              defaultMessage: 'Ready',
            })}
          />
        </LabelS>
      )}

      {!canBeDisplayed && '…'}
    </Vertical>
  );
};

FreeTicket.fragments = {
  TaskInterface: gql`
    fragment FreeTicket_TaskInterface on TaskInterface {
      id
      aasmState
      completedAt
    }
  ` as TypedDocumentNode<FreeTicket_TaskInterface>,
};
